import {
  colorHarmonyService,
  ColorCompatibilityResult as HarmonyResult,
} from "./colorHarmonyService";
import {
  getColorCompatibilityScore as getRuleBasedScore,
  ColorCompatibilityResult,
} from "../utils/colorUtils";

export interface HybridColorCompatibilityResult
  extends ColorCompatibilityResult {
  harmonyScore: number;
  ruleBasedScore: number;
  hybridScore: number;
  confidence: number;
  method: "harmony" | "rules" | "hybrid";
}

export class HybridColorService {
  private harmonyWeight = 0.7; // Weight for color harmony (70%)
  private rulesWeight = 0.3; // Weight for rule-based logic (30%)
  private confidenceThreshold = 0.4; // Threshold for preferring harmony over rules

  getHybridCompatibilityScore(
    color1: { r: number; g: number; b: number },
    color2: { r: number; g: number; b: number },
    context?: {
      garmentTypes?: string[];
      season?: string;
      occasion?: string;
    }
  ): HybridColorCompatibilityResult {
    try {
      // Get color harmony-based score
      const harmonyResult = colorHarmonyService.calculateCompatibility(
        color1,
        color2,
        {
          garmentTypes: context?.garmentTypes || [],
          season: context?.season as "spring" | "summer" | "fall" | "winter",
          occasion: context?.occasion as
            | "casual"
            | "business"
            | "formal"
            | "party",
        }
      );
      const harmonyScore = harmonyResult.score;

      // Get rule-based score
      // Convert color objects to string arrays for the existing function
      const color1String = `rgb(${color1.r},${color1.g},${color1.b})`;
      const color2String = `rgb(${color2.r},${color2.g},${color2.b})`;

      // Debug: Check for NaN values in RGB
      if (isNaN(color1.r) || isNaN(color1.g) || isNaN(color1.b)) {
        console.warn(`🚨 NaN detected in color1:`, color1);
      }
      if (isNaN(color2.r) || isNaN(color2.g) || isNaN(color2.b)) {
        console.warn(`🚨 NaN detected in color2:`, color2);
      }

      const ruleBasedResult = getRuleBasedScore(
        [color1String],
        [color2String],
        context?.garmentTypes?.map((type) => ({ type }))
      );
      const ruleBasedScore = ruleBasedResult.score;

      // Calculate confidence based on agreement between models
      const scoreDifference = Math.abs(harmonyScore - ruleBasedScore);
      const confidence = Math.max(0, 1 - scoreDifference);

      // Determine which method to use - heavily favor color harmony
      let finalScore: number;
      let method: "harmony" | "rules" | "hybrid";

      if (harmonyResult.confidence >= this.confidenceThreshold) {
        // High harmony confidence - trust color harmony model
        finalScore = harmonyScore;
        method = "harmony";
      } else if (confidence >= this.confidenceThreshold) {
        // Moderate confidence - use harmony with rule-based influence
        finalScore =
          harmonyScore * this.harmonyWeight + ruleBasedScore * this.rulesWeight;
        method = "hybrid";
      } else {
        // Low confidence - still prefer harmony but with more rule-based influence
        finalScore = harmonyScore * 0.6 + ruleBasedScore * 0.4;
        method = "hybrid";
      }

      // Enhance explanation with harmony insights
      const enhancedExplanation = this.enhanceExplanation(
        ruleBasedResult.explanation,
        harmonyScore,
        confidence,
        method,
        harmonyResult
      );

      return {
        score: finalScore,
        explanation: enhancedExplanation,
        harmonyScore,
        ruleBasedScore,
        hybridScore: finalScore,
        confidence,
        method,
        // Include other properties from rule-based result
        details: ruleBasedResult.details,
      } as HybridColorCompatibilityResult;
    } catch (error) {
      // Last resort fallback to rule-based logic only
      const color1String = `rgb(${color1.r},${color1.g},${color1.b})`;
      const color2String = `rgb(${color2.r},${color2.g},${color2.b})`;

      // Debug: Check for NaN values in RGB (fallback case)
      if (isNaN(color1.r) || isNaN(color1.g) || isNaN(color1.b)) {
        console.warn(`🚨 NaN detected in color1 (fallback):`, color1);
      }
      if (isNaN(color2.r) || isNaN(color2.g) || isNaN(color2.b)) {
        console.warn(`🚨 NaN detected in color2 (fallback):`, color2);
      }

      const ruleBasedResult = getRuleBasedScore(
        [color1String],
        [color2String],
        context?.garmentTypes?.map((type) => ({ type }))
      );
      return {
        ...ruleBasedResult,
        harmonyScore: 0.5, // Neutral harmony score since harmony failed
        ruleBasedScore: ruleBasedResult.score,
        hybridScore: ruleBasedResult.score,
        confidence: 0,
        method: "rules" as const,
      } as HybridColorCompatibilityResult;
    }
  }

  private enhanceExplanation(
    originalExplanation: string[],
    harmonyScore: number,
    confidence: number,
    method: string,
    harmonyResult: HarmonyResult
  ): string[] {
    let enhancedExplanation = [...originalExplanation];

    // Add harmony insights
    if (method === "harmony") {
      enhancedExplanation.push(
        ` Color Harmony Analysis: ${Math.round(
          harmonyScore * 100
        )}% compatibility with ${Math.round(
          harmonyResult.confidence * 100
        )}% confidence.`
      );
    } else if (method === "hybrid") {
      enhancedExplanation.push(
        ` Harmony-dominant analysis: ${Math.round(
          harmonyScore * 100
        )}% harmony score with ${Math.round(confidence * 100)}% agreement.`
      );
    } else if (method === "rules") {
      enhancedExplanation.push(
        ` Fallback to rule-based analysis (harmony model unavailable).`
      );
    }

    // Add harmony-specific insights
    enhancedExplanation.push(...harmonyResult.explanation);

    return enhancedExplanation;
  }

  // Method to adjust weights based on performance
  adjustWeights(harmonyPerformance: number, rulesPerformance: number) {
    // Adjust weights based on which method performs better
    const totalPerformance = harmonyPerformance + rulesPerformance;

    if (totalPerformance > 0) {
      this.harmonyWeight = harmonyPerformance / totalPerformance;
      this.rulesWeight = rulesPerformance / totalPerformance;
    }
  }

  // Method to get model statistics
  getModelStats() {
    return {
      harmonyWeight: this.harmonyWeight,
      rulesWeight: this.rulesWeight,
      confidenceThreshold: this.confidenceThreshold,
    };
  }
}

// Singleton instance
export const hybridColorService = new HybridColorService();
